/**
 * Custom hook that encapsulates shape interaction logic
 *
 * Provides shape click/selection/synchronization logic used in CandlestickChart.
 * Can also be reused in engview's candle-chart.
 */
import { useStore } from 'zustand';
import { useChartShapes } from '../contexts/ChartShapesContext';
import { useGlobalShapeSelector } from '../contexts/GlobalShapesContext';
import { useChartUIContext } from '../contexts/ChartUIContext';
import { useChartUIStoreSelector } from '../stores/chartUIStore';
import { emptyMeasureArray } from '../utils/shapeHelpers';
import type { ShapeSelection, MoreProps } from '../types/commontypes';

interface UseShapeInteractionParams {
  /** Current chart's symbol */
  symbol: string;
  /** Current chart's timeframe */
  timeframe: string;
}

/**
 * Shape interaction hook
 *
 * Features:
 * 1. Mouse down handler — measure cleanup, popup closing
 * 2. Click handler — background click deselection (fires on mouseup, AFTER shape's onSelect)
 */
export const useShapeInteraction = ({ symbol: _symbol, timeframe: _timeframe }: UseShapeInteractionParams) => {
  const { shapeStore, drawState } = useChartShapes();
  const renderNodes = useStore(shapeStore, s => s.renderNodes);
  const setRenderNodes = useStore(shapeStore, s => s.setRenderNodes);
  const setSelectedShape = useGlobalShapeSelector(s => s.setSelectedShape);
  const { chartUIStore } = useChartUIContext();
  const activePopup = useChartUIStoreSelector(chartUIStore, s => s.activePopup);
  const setActivePopup = useChartUIStoreSelector(chartUIStore, s => s.setActivePopup);
  const setChartClicked = useChartUIStoreSelector(chartUIStore, s => s.setChartClicked);

  /**
   * Mouse down and pan end handler
   *
   * Only performs housekeeping (measure cleanup, popup closing).
   * Does NOT handle deselection — that's done in handleClick (onClick)
   * to avoid interfering with the library's mousedown→mouseup click detection.
   */
  const handleMouseDownAndPanEnd = (_e: React.MouseEvent<Element, MouseEvent>, _moreProps: MoreProps) => {
    const clearedMeasures = emptyMeasureArray(renderNodes);
    if (clearedMeasures !== renderNodes) {
      setRenderNodes(clearedMeasures);
    }

    setChartClicked(true);
    if (activePopup?.type === 'y-popup' || activePopup?.type === 'order-popup') setActivePopup(null);
  };

  /**
   * Click handler — deselects shape on background click.
   *
   * ChartSection uses shapeJustSelectedRef (set by ShapeRender.onSelect/onComplete
   * during mousedown) to determine whether a shape was clicked.
   * If a shape was clicked, currentSelection contains the shape info.
   * If background was clicked, currentSelection is null → deselect.
   */
  const handleClick = <T = unknown>(
    _e: React.MouseEvent<Element, MouseEvent>,
    _moreProps: MoreProps<T>,
    currentSelection: ShapeSelection | null,
  ) => {
    if (drawState.status === 'started') return;

    if (currentSelection) {
      // Shape was clicked → keep selection (already set by onSelect)
      return;
    }

    // Background click → deselect
    setSelectedShape(null);
  };

  return {
    handleMouseDownAndPanEnd,
    handleClick,
  };
};
